import { Fragment, useEffect, useState } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { ArchiveBoxIcon } from "@heroicons/react/24/outline";

/**
 * Admin-only: required comment before archiving a library document request.
 */
export default function ArchiveLibraryRequestModal({
  open,
  onClose,
  onConfirm,
  isSubmitting = false,
  requestLabel = "",
}) {
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (open) setComment("");
  }, [open]);

  const handleClose = () => {
    setComment("");
    onClose();
  };

  const handleConfirm = () => {
    const trimmed = comment.trim();
    if (!trimmed) return;
    onConfirm(trimmed);
  };

  return (
    <Transition show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[100]" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500/40 transition-opacity" />
        </TransitionChild>
        <div className="fixed inset-0 z-[100] w-screen overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <TransitionChild
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 sm:mx-0 sm:h-10 sm:w-10">
                    <ArchiveBoxIcon
                      className="h-6 w-6 text-slate-600"
                      aria-hidden
                    />
                  </div>
                  <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                    <DialogTitle
                      as="h3"
                      className="text-base font-semibold leading-6 text-gray-900"
                    >
                      Archive Request
                    </DialogTitle>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Please provide a brief reason for archiving this
                        request.
                      </p>
                      {requestLabel ? (
                        <p className="mt-1 text-sm text-gray-700">
                          Request:{" "}
                          <span className="font-medium">{requestLabel}</span>
                        </p>
                      ) : null}
                    </div>
                  </div>
                </div>
                <div className="mt-4">
                  <label
                    htmlFor="archive-reason"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Archive Reason <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    id="archive-reason"
                    rows={4}
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                    placeholder="Enter reason..."
                  />
                </div>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse sm:gap-2">
                  <button
                    type="button"
                    disabled={isSubmitting || !comment.trim()}
                    onClick={handleConfirm}
                    className="inline-flex w-full justify-center rounded-md bg-slate-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-slate-800 disabled:opacity-50 sm:w-auto"
                  >
                    {isSubmitting ? "Archiving…" : "Archive"}
                  </button>
                  <button
                    type="button"
                    onClick={handleClose}
                    disabled={isSubmitting}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
